import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";

export interface ManagerNoteSurfaceProps {
  note: string;
  /** Metadata rendered after the "Manager note" label (a timestamp, or "Applies next week"). */
  metadata?: ReactNode;
  /** Hover-revealed edit action; omitted for read-only viewers. */
  onEdit?: () => void;
  /** Hover-revealed delete action; omitted for read-only viewers. */
  onDelete?: () => void;
}

/**
 * The amber "sticky note" surface a manager's note renders on, shown above the
 * AI coaching note. Edit/delete actions reveal on hover or focus.
 */
export const ManagerNoteSurface: FC<ManagerNoteSurfaceProps> = (props) => {
  const { note, metadata, onEdit, onDelete } = props;

  return (
    <div className="group rounded-lg border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-6 whitespace-pre-wrap text-foreground">
            {note}
          </p>

          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Manager note</span>
            {metadata ? (
              <>
                <span aria-hidden>·</span>
                <span>{metadata}</span>
              </>
            ) : null}
          </div>
        </div>

        {onEdit || onDelete ? (
          <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            {onEdit ? (
              <IconButton
                className="hover:bg-amber-100"
                variant={ButtonVariant.Tertiary}
                size={Size.SMALL}
                accessibleLabel="Edit manager note"
                icon={<IconPencil className="size-4" />}
                onClick={onEdit}
              />
            ) : null}
            {onDelete ? (
              <IconButton
                className="hover:bg-amber-100"
                variant={ButtonVariant.Tertiary}
                size={Size.SMALL}
                accessibleLabel="Delete manager note"
                icon={<IconTrash className="size-4" />}
                onClick={onDelete}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
